import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { invitationApi } from 'src/api/invitation';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { translateKey } from 'src/locales/translate';


interface OrganizationInvitesResendModalProps {
  fetchInvites: () => void;
  invitationId: string;
}

export default function OrganizationInvitesResendModal({
  fetchInvites,
  invitationId,
}: OrganizationInvitesResendModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const { organizationId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: resendInvitation } = useMutation(
    ['organization-invite-resend', organizationId],
    () =>
      invitationApi.invitationControllerResendInvitation({
        invitationId,
      }),
    {
      retry: 2,
    },
  );

  const closeModal = () => setIsOpen(false);

  const openModal = () => setIsOpen(true);

  const handleResend = async () => {
    setIsResendLoading(true);
    try {
      await resendInvitation();
      closeModal();
      enqueueSnackbar(translateKey("hardcoded.zaproszenie_zostalo_wyslane"), {
        variant: 'success',
      });
      fetchInvites();
    } catch (error) {
      enqueueSnackbar(error.message || error, {
        variant: 'error',
      });
    } finally {
      setIsResendLoading(false);
    }
  };

  return (
    <>
      <IconButton
        data-testid="invite-resend-open"
        onClick={(e) => {
          e.stopPropagation();
          openModal();
        }}
      >
        <Iconify icon="ic:outline-email" />
      </IconButton>
      <Dialog open={isOpen} onClose={closeModal}>
        <DialogTitle>{translateKey("hardcoded.wyslij_ponownie_zaproszenie")}</DialogTitle>

        <DialogContent sx={{ maxWidth: '400px' }}>
          <DialogContentText id="alert-dialog-description">{translateKey("hardcoded.czy_na_pewno_chcesz_wyslac_ponownie_zaproszenie_do")}</DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={closeModal}>{translateKey("common.cancel")}</Button>
          <LoadingButton
            variant="contained"
            data-testid="invite-resend-confirm"
            onClick={handleResend}
            loading={isResendLoading}
          >
            <span>{translateKey("hardcoded.wyslij")}</span>
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
